<template>
    <v-card :height="gridHeight" elevation="0">
        <div id="GridParent" style="height: 100%">
            <ejs-grid ref="byCompanyViewGrid" :load="load" :frozenColumns="2" :dataSource="dataSource"
                :dataBound="dataBound" :beforeDataBound="beforeDataBound" :enableAdaptiveUI="false" height="100%"
                :allowSelection="false" :allowResizing="true" :allowSorting="true" :allowMultiSorting="false"
                :enableHover="false" width="100%" gridLines="Both" :allowTextWrap="true"
                :textWrapSettings="wrapSettings" :columns="columns" :actionBegin="actionBegin"
                :actionComplete="actionComplete">
            </ejs-grid>
        </div>
    </v-card>
</template>

<script>
import Vue from "vue";
import { mapState } from "vuex";
import {
    GridPlugin,
    Freeze,
    Resize,
    Sort,
    Page,
    Search,
} from "@syncfusion/ej2-vue-grids";
import AccountDataColTemplate from "./AccountDataColTemplate.vue";
import CompanyStackedColumnTemplate from "./CompanyStackedColumnTemplate.vue";
import CompanyAccountHeaderTemplate from "./CompanyAccountHeaderTemplate.vue";
import AccountFrozenRowTemplate from "./AccountFrozenRowTemplate.vue";
Vue.use(GridPlugin);

Vue.prototype.$eventHub = new Vue();
export default {
    name: "LCAGridView",
    provide: {
        grid: [Freeze, Resize, Sort, Page, Search],
    },
    components: {
        AccountDataColTemplate,
        CompanyStackedColumnTemplate,
        CompanyAccountHeaderTemplate,
        AccountFrozenRowTemplate,
    },
    props: {
        gridHeight: { type: Number, default: window.innerHeight - 255 }, lcaData: { type: Array }
    },
    data() {
        return {
            companyNames: [],
            paginatedResult: [],
            pageCount: 0,
            itemsPerPage: 1,
            page: 1,
            fiscalTermsList: [],
            selectedFiscalTerms: null,
            selectedComparedTo: null,
            displayMessage: false,
            message: "",
            messageType: "",
            fileType: null,
            file_accept: ["text/plain", ".tsv", ""],
            valid: false,
            loading: false,
            uploadingFiles: [],
            uploadDialog: false,
            isOpen: false,
            selectedCompanies: [],
            companyList: [],
            pageSettings: { pageSize: 50 },
            isInvalidLCAFile: false,
            dataSource: [],
            wrapSettings: { wrapMode: "Content" },
            accountRowTemplate: (cell, col) => {
                return () => {
                    return {
                        template: {
                            parent: this,
                            extends: AccountDataColTemplate,
                            data() {
                                return {
                                    data: {},
                                };
                            },
                            propsData: { cell: cell, col: col },
                        },
                    };
                };
            },
            companyStackedColumn: function (title, sequence) {
                return () => {
                    return {
                        template: {
                            extends: CompanyStackedColumnTemplate,
                            data() {
                                return {
                                    data: {},
                                };
                            },
                            propsData: { companyName: title, sequence: sequence },
                        },
                    };
                };
            },
            companyAccountHeaderColumn: function (accountCol) {
                return () => {
                    return {
                        template: {
                            extends: CompanyAccountHeaderTemplate,
                            data() {
                                return {
                                    data: {},
                                };
                            },
                            propsData: { accountCol: accountCol },
                        },
                    };
                };
            },
            accountFrozenRow: (cell, col) => {
                return () => {
                    return {
                        template: {
                            parent: this,
                            extends: AccountFrozenRowTemplate,
                            data() {
                                return {
                                    data: {},
                                };
                            },
                            propsData: { cell: cell, col: col },
                        },
                    };
                };
            },
        };
    },
    computed: {
        ...mapState({
            lcaFiscalTerms: (state) => state.agenda.lcaFiscalTerms,
            lcaCompanies: (state) => state.agenda.lcaCompanies,
            currentLCATab: (state) => state.agenda.currentLCATab,
        }),
        columns() {
            let firstCol = [];
            firstCol.push(
                {
                    field: "Account(Code)",
                    headerText: this.$t('business.lblAccountCode'),
                    allowSorting: false,
                    template: this.accountFrozenRow(
                        '', "Account(Code)"),
                    width: 150,
                },
                {
                    field: "Account(Name)",
                    headerText: this.$t('business.lblAccountName'),
                    allowSorting: false,
                    template: this.accountFrozenRow(
                        '', "Account(Name)"),
                    width: 270,
                },
            );

            this.lcaRes?.forEach((company) => {
                const fieldName = this.currentLCATab === 0 ? 'Consolidated Amount' : 'Parent Basis Amount'
                firstCol.push(
                    {
                        field: "Amount in Previous Term",
                        allowSorting: false,
                        headerTemplate: this.companyStackedColumn(
                            company["Company(Name)"],
                            1
                        ),
                        columns: [
                            {
                                field: "Amount in Previous Term",
                                template: this.accountRowTemplate(
                                    company?.data, 'Amount in Previous Term'
                                ),
                                headerTemplate: this.companyAccountHeaderColumn(
                                    this.$t('business.lblAmountInPreviousTerm')
                                ),
                                width: 120,
                                allowSorting: false,
                            },
                        ]
                    },
                    {
                        field: fieldName,
                        allowSorting: false,
                        headerTemplate: this.companyStackedColumn(
                            company["Company(Name)"],
                            0
                        ),
                        columns: [
                            {
                                field: fieldName,
                                template: this.accountRowTemplate(
                                    company?.data, fieldName
                                ),
                                headerTemplate: this.companyAccountHeaderColumn(
                                    this.$t('business.lblBasisAmount')
                                ),
                                width: 120,
                                allowSorting: false,
                            },
                        ]
                    },
                    {
                        field: "Previous Term Difference",
                        allowSorting: false,
                        headerTemplate: this.companyStackedColumn(
                            company["Company(Name)"],
                            0
                        ),
                        columns: [
                            {
                                field: "Previous Term Difference",
                                template: this.accountRowTemplate(
                                    company?.data, 'Previous Term Difference'
                                ),
                                headerTemplate: this.companyAccountHeaderColumn(
                                    this.$t('business.lblPreviousTermDifference')
                                ),
                                width: 120,
                                allowSorting: false,
                            },
                        ]
                    },
                    {
                        field: "Previous Term Ratio",
                        allowSorting: false,
                        headerTemplate: this.companyStackedColumn(
                            company["Company(Name)"],
                            0
                        ),
                        columns: [
                            {
                                field: "Previous Term Ratio",
                                template: this.accountRowTemplate(
                                    company?.data, 'Previous Term Ratio'
                                ),
                                headerTemplate: this.companyAccountHeaderColumn(
                                    this.$t('business.lblPreviousTermRatio')
                                ),
                                width: 120,
                                allowSorting: false,
                            },
                        ]
                    },
                )
                this.dataSource = company.data  
            });
            return firstCol;
        },
        LoaderText() {
            return this.$t("LoaderText");
        },
        lcaRes() {
            if (!this.lcaData) return [];

            return this.lcaData
                .map(item => ({
                    ...item,
                    data: [...item.data].sort((a, b) => 
                        a["Account(Sort Number)"] - b["Account(Sort Number)"]
                    )
                }))
                .sort((a, b) => 
                    a['Company(Name)']?.localeCompare(b['Company(Name)'])
                );
        }
    },

    methods: {
        load() {
            this.$refs.byCompanyViewGrid.ej2Instances.defaultLocale.EmptyRecord = "";
        },
        dataBound() {
            this.loaderStop();
        },
        beforeDataBound: function (args) {
            //this.loading = true;
            this.loaderStart();
        },

        actionBegin(args) {
            this.loaderStart();
        },
        async actionComplete(args) {
            await this.loaderStop();
            let stackedCompanyNameEl = document.getElementsByClassName('e-stackedheadercell')
            this.$nextTick(() => {
                for (let index = 0; index < stackedCompanyNameEl.length; index++) {
                    let el = stackedCompanyNameEl[index]
                    if (el.firstChild.classList.contains('e-stackedheadercelldiv') && el.firstChild.firstChild.id === 'StackedCompanyName') {
                        el.style.borderLeft = '1px solid var(--v-secondary-darken1)';
                    }
                    else {
                        el.style.borderLeft = 'none'
                    }
                }
            });
        },

        async sleep(ms) {
            return new Promise((resolve) => setTimeout(resolve, ms));
        },
        loaderStart() {
            this.loading = true;
            if (
                this.$refs.byCompanyViewGrid &&
                this.$refs.byCompanyViewGrid.$el.classList
            ) {
                this.$refs.byCompanyViewGrid.$el.classList.add("disablegrid");
                let gridElement = document.getElementById("GridParent");
                if (gridElement.classList) {
                    gridElement.classList.add("wrapper");
                }
            }
        },
        async loaderStop() {
            await this.sleep(2000);
            if (
                this.$refs.byCompanyViewGrid &&
                this.$refs.byCompanyViewGrid.$el.classList &&
                this.$refs.byCompanyViewGrid.$el.classList.contains("disablegrid") && this.lcaRes.length
            ) {
                this.$refs.byCompanyViewGrid.$el.classList.remove("disablegrid");
                let gridElement = document.getElementById("GridParent");
                if (gridElement.classList) {
                    gridElement.classList.remove("wrapper");
                }
            }
            this.loading = false;
        },
    },
};
</script>

<style scoped>
.v-toolbar /deep/ .v-toolbar__content {
    color: var(--v-darkCharcoal-base);
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 21px;
}

::v-deep .disablegrid {
    pointer-events: none;
    opacity: 0.9;
}

::v-deep #GridParent .e-grid .e-movableheader .e-headercell {
    /* height: 30px; */
    background-color: var(--v-secondary-base);
    border-left: 1px solid var(--v-secondary-darken1);
    color: var(--v-darkCharcoal-base) !important;
}

::v-deep #GridParent .e-grid .e-gridheader .e-headercontent .e-columnheader .e-headercell .e-headertext {
    color: #FFFFFF !important;
    font-size: 14px;
}

::v-deep #GridParent .e-grid .e-headercelldiv,
::v-deep #GridParent .e-grid .e-stackedheadercelldiv {
    font-family: "Noto Sans JP" !important;
    font-style: normal !important;
    font-weight: 200 !important;
    font-size: 13px !important;
    padding: 0 !important;
    text-align: center;
}

::v-deep #GridParent .e-grid .e-gridheader {
    background-color: var(--v-secondary-base);
}

::v-deep #GridParent .e-grid .e-frozenheader {
    background-color: var(--v-neutralGray-base) !important;
}

::v-deep #GridParent .e-grid .e-frozenheader .e-sortfilterdiv {
    display: none !important;
    height: 0px;
    width: 0px;
}

::v-deep #GridParent .e-grid .e-gridheader .e-frozenheader .e-headercell {
    background-color: var(--v-neutralGray-base) !important;
    color: var(--v-secondary-lighten2) !important;
}

::v-deep #GridParent .e-grid .e-gridheader .e-frozenheader .e-headercelldiv {
    padding: 0px !important;
}

::v-deep #GridParent .e-grid .e-movablecontent .e-rowcell {
    padding: 8px;
}

::v-deep #GridParent .e-grid .e-frozencontent td.e-rowcell {
    padding: 0px !important;
    vertical-align: middle !important;
}

.e-grid .e-content.c-scroll {
    overflow-y: auto;
}

::v-deep #GridParent .e-table,
.e-grid .e-frozencontent .e-virtualtable>.e-table,
#GridParent .e-grid .e-frozenheader .e-virtualtable>.e-table {
    border-right-color: var(--v-secondary-base) !important;
}

.e-grid .e-frozencontent td.e-rowcell {
    background-color: var(--v-Natural-lighten4) !important;
}

::v-deep #GridParent .e-grid .e-movablecontent {
    background-color: var(--v-Neutral-base) !important;
}

::v-deep .e-spinner-pane {
    animation: material-spinner-rotate 1568.63ms linear infinite;
    display: None;
    margin: 0 auto;
}

::v-deep .e-spinner-pane .e-spinner-inner .e-spin-material {
    animation: material-spinner-rotate 1568.63ms linear infinite;
    display: None;
    margin: 0 auto;
}

::v-deep .agenda-card {
    background: #ffffff;
    box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.1) !important;
    border-radius: 4px;
}

::v-deep .agenda-card .text-subtitle-1 {
    font-family: "Noto Sans JP" !important;
    font-style: normal !important;
}

::v-deep .agenda-card .v-card__text {
    padding: 12px !important;
    color: var(--v-darkCharcoal-base) !important;
}

::v-deep .agenda-card .text--accountData {
    font-family: "Noto Sans JP";
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    /* color: var(--v-darkCharcoal-base); */
}

::v-deep .agenda-card .accountData-grid-display {
    display: grid;
    grid-gap: 14px;
}

::v-deep .card-grid-display {
    display: grid;
    grid-gap: 8px;
}

::v-deep .e-grid .e-gridheader table th[rowspan],
::v-deep .e-grid.e-device .e-gridheader table th[rowspan] {
    vertical-align: middle !important;
}

::v-deep .e-columnheader:has(.e-stackedheadercell) th.e-stackedheadercell {
    background-color: var(--v-neutralGray-base) !important;
}

::v-deep #GridParent .e-grid .e-stackedheadercell {
    border-left: none;
}

::v-deep .e-grid .e-gridheader table th[rowspan],
.e-grid.e-device .e-gridheader table th[rowspan] {
    padding-bottom: 0 !important;
}
</style>